"use client";

/**
 * useDiagnosticsHistory — loads saved DiagnosticsSession records for the current user.
 *
 * Mirrors React's useDiagnosticsData hook:
 *   - Authenticated: queries Firestore (owner + collaborator sessions)
 *   - Unauthenticated: reads from localStorage
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/platform/auth/useAuth";
import {
  getUserSessions,
  getCollaboratorSessions,
  getSessionsByInvitedEmail,
  resolveCollaboratorUid,
  updateSession,
  deleteSession,
} from "@/platform/firebase/firestore/diagnostics";
import type { DiagnosticsSession } from "@/modes/diagnostics/types";

interface UseDiagnosticsHistoryResult {
  sessions:  DiagnosticsSession[];
  loading:   boolean;
  error:     string | null;
  refresh:   () => void;
  toggleStar:    (id: string) => void;
  toggleArchive: (id: string) => void;
  deleteRun:     (id: string) => void;
}

export function useDiagnosticsHistory(): UseDiagnosticsHistoryResult {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<DiagnosticsSession[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!user) {
      // Unauthenticated: read from localStorage
      try {
        const raw = localStorage.getItem("savedDiagnostics");
        if (raw) {
          setSessions(JSON.parse(raw) as DiagnosticsSession[]);
        } else {
          setSessions([]);
        }
      } catch {
        setSessions([]);
      }
      setLoading(false);
      return;
    }

    try {
      // Resolve pending collaborator invites first (same as React)
      if (user.email) {
        const invited = await getSessionsByInvitedEmail(user.email);
        const pending = invited.filter((s) =>
          (s.shared_with ?? []).some((c) => c.email === user.email && c.uid === null)
        );
        if (pending.length > 0) {
          await Promise.all(
            pending.map((s) => resolveCollaboratorUid(s.id, user.email!, user.uid))
          );
        }
      }

      const [ownerSessions, collabSessions] = await Promise.all([
        getUserSessions(user.uid),
        getCollaboratorSessions(user.uid),
      ]);

      // Merge, avoiding duplicates
      const ownerIds = new Set(ownerSessions.map((s) => s.id));
      const merged   = [
        ...ownerSessions,
        ...collabSessions.filter((s) => !ownerIds.has(s.id)),
      ];

      setSessions(merged);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load diagnostics");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const toggleStar = useCallback((id: string) => {
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        const updated = { ...s, isStarred: !s.isStarred };
        if (user) updateSession(id, { isStarred: updated.isStarred }).catch(console.error);
        return updated;
      })
    );
  }, [user]);

  const toggleArchive = useCallback((id: string) => {
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== id) return s;
        const updated = { ...s, isArchived: !s.isArchived };
        if (user) updateSession(id, { isArchived: updated.isArchived }).catch(console.error);
        return updated;
      })
    );
  }, [user]);

  const deleteRun = useCallback((id: string) => {
    setSessions((prev) => prev.filter((s) => s.id !== id));
    if (user) deleteSession(id).catch(console.error);
  }, [user]);

  return { sessions, loading, error, refresh: load, toggleStar, toggleArchive, deleteRun };
}
